import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Lock, Mail, User, KeyRound } from 'lucide-react';
import { API_URL } from '../config';

const API = API_URL;

const AuthPage = ({ onAuth }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    document.title = 'EbonyThroatQueens | Sign In';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      onAuth(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: resetEmail
      });
      
      toast.success('If the email exists, a password reset link has been sent.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      toast.success(response.data.message);
      setShowResetPassword(false);
      setResetToken('');
      setNewPassword('');
      setIsLogin(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        {/* Logo + Title */}
        <div className="text-center mb-8">
          {/* Logo Image */}
          <div className="flex justify-center mb-4">
            <div 
              className="w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center overflow-hidden" 
              style={{ 
                background: 'linear-gradient(135deg, #EE1C4E 0%, #FF6B9D 100%)',
                boxShadow: '0 8px 32px rgba(238, 28, 78, 0.3)'
              }}
            >
              <img 
                src="https://pub-cdd5f0ce51164f909259eb7b0c79f5fe.r2.dev/Logo/make-the-background-black_zpiRh4l_R4W9wlUEJELlMQ_KCzmZlO2RJymLtGIynsdPw.jpeg" 
                alt="EbonyThroatQueens Logo"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          
          {/* Title */}
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-4xl md:text-5xl font-black" style={{ color: '#EE1C4E', letterSpacing: '-0.02em' }} data-testid="auth-logo">
              Ebony
            </h1>
            <h1 className="text-4xl md:text-5xl font-black" style={{ color: '#EE1C4E', letterSpacing: '-0.02em' }}>
              ThroatQueens
            </h1>
          </div>
          <p className="text-gray-300 text-base font-medium italic mt-3" style={{ fontFamily: 'Georgia, serif' }}>
            Behind Velvet Curtains
          </p>
        </div>

        {/* Auth Form */}
        <div className="rounded-2xl p-8" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-lg p-1" style={{ background: '#1A1A1A' }}>
              <button
                onClick={() => setIsLogin(true)}
                className="px-6 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: isLogin ? '#EE1C4E' : 'transparent',
                  color: isLogin ? 'white' : '#A1A1AA'
                }}
                data-testid="login-tab"
              >
                Login
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className="px-6 py-2 rounded-lg text-sm font-semibold transition-all"
                style={{
                  background: !isLogin ? '#EE1C4E' : 'transparent',
                  color: !isLogin ? 'white' : '#A1A1AA'
                }}
                data-testid="signup-tab"
              >
                Sign Up
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-sm text-gray-300 mb-2 block">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white focus:border-[#EE1C4E]"
                    placeholder="Enter your name"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm text-gray-300 mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white focus:border-[#EE1C4E]"
                  placeholder="Enter your email"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm text-gray-300">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-[#EE1C4E] hover:underline"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white focus:border-[#EE1C4E]"
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full py-6 text-base font-semibold"
              style={{ background: '#EE1C4E', color: 'white' }}
              disabled={loading}
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Login' : 'Create Account'}
            </Button>
          </form>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label className="text-sm text-gray-300 mb-2 block">Email Address</Label>
              <Input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                placeholder="Enter your email"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              style={{ background: '#EE1C4E', color: 'white' }}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Set New Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label className="text-sm text-gray-300 mb-2 block">Reset Token</Label>
              <Input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                placeholder="Enter reset token"
                required
              />
            </div>
            <div>
              <Label className="text-sm text-gray-300 mb-2 block">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                placeholder="Enter new password"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              style={{ background: '#EE1C4E', color: 'white' }}
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;
